/**
 * Calendar Service
 *
 * Read-only access to a user's calendar events for a given month. Events are
 * point-in-time entries — scheduled/completed workouts and plan/challenge/goal
 * start & end milestones. See `CalendarEvent`.
 */

import { apiClient } from './api/client';
import { ApiErrorClass } from '../types/api/errors';

import type { GetApiResponse } from '../types/api.types';
import type { CalendarEvent } from '../types/domain/calendar.types';

/**
 * Calendar Service Class
 */
export class CalendarService {
  /**
   * Fetch a user's calendar events for a single month.
   *
   * @param username - Username whose calendar to read
   * @param month - Target month in `YYYY-MM` format
   */
  async getCalendarEvents(
    username: string,
    month: string
  ): Promise<CalendarEvent[]> {
    try {
      const response = await apiClient.get<GetApiResponse<CalendarEvent[]>>(
        `/user/${username}/calendar/events`,
        { params: { month } }
      );

      // Laravel success responses return 200 with data wrapped in 'data' key
      if (response.status === 200 && response.data.data) {
        return response.data.data;
      }

      const message =
        response.data.message || 'Failed to fetch calendar events';
      throw new ApiErrorClass(message, 'BAD_REQUEST', response.status);
    } catch (error) {
      if (error instanceof ApiErrorClass) {
        throw error;
      }

      throw new ApiErrorClass(
        'Failed to fetch calendar events',
        'SERVER_ERROR',
        500
      );
    }
  }
}

// Export singleton instance
export const calendarService = new CalendarService();

// Export class for testing
export default CalendarService;
